/**
 * Cleaning for email addresses that were pasted rather than typed.
 *
 * An address copied out of Outlook, Word, a PDF, or a signature block routinely
 * carries characters that look exactly like the ASCII ones but are not: a
 * non-breaking space, a zero-width space, an en dash where a hyphen belongs, a
 * one-dot leader where a period belongs. The browser's own `type="email"`
 * validation rejects these, and the message it produces names the offending
 * character — which renders indistinguishably from the ordinary one:
 *
 *     A part followed by '@' should not contain the symbol '.'.
 *
 * That is unreadable as a bug report. It got filed against us as "email
 * validation rejects hyphens", because the address that failed happened to have
 * a hyphenated domain, and nothing about the message points at an invisible
 * character. Hyphens were never the problem, and no validator in this codebase
 * has ever rejected one.
 *
 * So rather than loosening validation — the invisible characters really are
 * invalid, and the server is right to refuse them — normalize the input to the
 * ASCII the user believes they typed, before anything gets a chance to complain.
 */

// Invisible: zero-width space/non-joiner/joiner, word joiner, BOM, and the
// bidirectional marks that survive a copy out of a right-to-left document.
// Written as escapes on purpose — as literal characters these are unreadable in
// a diff, and an editor or tool that strips them silently changes the rule.
const INVISIBLE = /[\u200B-\u200F\u202A-\u202E\u2060\uFEFF]/g;

// Anything Unicode calls a space, including the non-breaking space a browser
// inserts when you copy from a rendered page. An email address has no legal
// unquoted space, so these are removed outright rather than collapsed — which
// also disposes of the ordinary leading/trailing space from a sloppy copy.
const ANY_SPACE = /[\s\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;

// Characters that render as a hyphen, a period, or an @ but are not.
const LOOKALIKES: Record<string, string> = {
  '\u2010': '-', // hyphen
  '\u2011': '-', // non-breaking hyphen
  '\u2012': '-', // figure dash
  '\u2013': '-', // en dash
  '\u2014': '-', // em dash
  '\u2015': '-', // horizontal bar
  '\u2212': '-', // minus sign
  '\uFF0D': '-', // fullwidth hyphen-minus
  '\uFF0E': '.', // fullwidth full stop
  '\u3002': '.', // ideographic full stop
  '\u2024': '.', // one-dot leader
  '\u06D4': '.', // arabic full stop
  '\uFF20': '@', // fullwidth commercial at
  '\uFE6B': '@'  // small commercial at
};

/**
 * Turn a pasted address into the plain-ASCII one the user meant.
 *
 * Deliberately conservative: it fixes transcription damage and unwraps the two
 * shapes people actually paste, and does nothing else. It does not lowercase
 * (the local part is case-sensitive per RFC 5321, however rarely that matters),
 * and it does not attempt to repair a genuinely malformed address — an address
 * that is wrong after this still gets rejected, which is correct.
 */
export function normalizeEmailInput(raw: string): string {
  if (!raw) return '';

  let value = raw.replace(INVISIBLE, '');

  // "Rally Cruz <info@example.com>" — the display-name form every mail client
  // puts on the clipboard. Keep what is inside the angle brackets.
  const bracketed = value.match(/<([^<>]+)>/);
  if (bracketed) value = bracketed[1];

  // A mailto: link dragged in from a web page.
  value = value.replace(/^\s*mailto:/i, '');

  for (const [from, to] of Object.entries(LOOKALIKES)) {
    value = value.split(from).join(to);
  }

  // Quotes a document may have wrapped around the address. Stripped only in
  // pairs, so a legitimately quoted local part is left alone.
  value = value.replace(/^["'\u2018\u2019\u201C\u201D]+|["'\u2018\u2019\u201C\u201D]+$/g, '');

  return value.replace(ANY_SPACE, '');
}

/**
 * Whether an address is well-formed, matching the rule the API enforces.
 *
 * The server validates with JSON Schema `format: "email"`, so a client check
 * that disagrees only produces a form that submits and then fails, or refuses
 * something the server would have accepted. This mirrors that rule: a local
 * part of permitted characters, then domain labels that are alphanumeric,
 * may contain inner hyphens, and may not begin or end with one.
 */
const EMAIL_FORMAT =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function isValidEmailFormat(value: string): boolean {
  return EMAIL_FORMAT.test(normalizeEmailInput(value));
}
